<template>
   <div>
      <h5 class="text-left q-ma-none q-mb-md">Application Tours</h5>

      <div v-if="tourLoading" class="text-center q-pa-md">
         <QSpinner color="primary" size="3em" />
         <div class="q-mt-sm">Loading tour information...</div>
      </div>

      <div v-else>
         <QTable
            :rows="toursData"
            :columns="tourColumns"
            flat
            row-key="id"
            :pagination="{ rowsPerPage: 10 }"
            class="tours-table"
         >
            <template #body-cell-status="props">
               <QTd :props="props">
                  <QChip
                     :color="props.row.completed ? 'positive' : 'grey-7'"
                     text-color="white"
                     dense
                  >
                     {{ props.row.completed ? "Completed" : "Incomplete" }}
                  </QChip>
               </QTd>
            </template>
            <template #body-cell-actions="props">
               <QTd :props="props">
                  <QBtn
                     flat
                     round
                     color="primary"
                     icon="mdi-refresh"
                     @click="resetTour(props.row.id)"
                  >
                     <QTooltip> Reset Tour </QTooltip>
                  </QBtn>
               </QTd>
            </template>
         </QTable>

         <div class="q-mt-lg">
            <QBtn
               color="primary"
               :loading="resetAllLoading"
               @click="resetAllTours"
            >
               Reset All Tours
            </QBtn>
         </div>
      </div>
   </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue"
import { QTable, QTd, QChip, QSpinner, QBtn, QTooltip } from "quasar"
import { useTourStore } from "@/stores/tourStore"
import { toast } from "vue3-toastify"
import { formatTourName, tours } from "@/tours"

// Interface for tour data in the table
interface TourDataRow {
   id: string
   name: string
   completed: boolean
}

// Tour panel refs
const tourLoading = ref(true)
const resetAllLoading = ref(false)
const toursData = ref<TourDataRow[]>([])
const tourStore = useTourStore()

// Tour columns definition for table
const tourColumns = [
   {
      name: "name",
      required: true,
      label: "Tour",
      align: "left" as const,
      field: "name",
      sortable: true,
   },
   {
      name: "status",
      required: true,
      label: "Status",
      align: "center" as const,
      field: "completed",
      sortable: true,
   },
   {
      name: "actions",
      required: true,
      label: "Actions",
      align: "center" as const,
      field: "actions",
   },
]
// Tour data comes from the central tours registry in src/tours/index.ts

// Load tour data from API
const loadTourData = async () => {
   tourLoading.value = true
   try {
      // Get tour completion status from API
      const tourCompletionStatus = await tourStore.getTourCompletionStatus()

      // Map tours from the central registry to UI-friendly format with only the fields we need
      // Filter out tours that have hideFromSettings flag
      toursData.value = tours
         .filter((tour) => !tour.hideFromSettings)
         .map((tour) => ({
            id: tour.id,
            name: tour.humanReadableName,
            completed: !!tourCompletionStatus[tour.id],
         }))

      // Add any tours from API that aren't in our central registry
      Object.keys(tourCompletionStatus).forEach((tourId) => {
         if (!tours.some((t) => t.id === tourId)) {
            toursData.value.push({
               id: tourId,
               name: formatTourName(tourId),
               completed: tourCompletionStatus[tourId],
            })
         }
      })
   } catch (error) {
      console.error("Failed to load tour data:", error)
      toast.error("Failed to load tour data")
   } finally {
      tourLoading.value = false
   }
}

// Using formatTourName from the central tours registry

// Reset a single tour
const resetTour = async (tourId: string) => {
   try {
      await tourStore.resetTour(tourId)

      // Update local state
      const tourIndex = toursData.value.findIndex((tour) => tour.id === tourId)
      if (tourIndex !== -1) {
         toursData.value[tourIndex].completed = false
      }

      toast.success(`Tour "${formatTourName(tourId)}" has been reset`)
   } catch (error) {
      console.error("Error resetting tour:", error)
      toast.error("Failed to reset tour")
   }
}

// Reset all tours
const resetAllTours = async () => {
   resetAllLoading.value = true
   try {
      await tourStore.resetAllTours()

      // Update local state
      toursData.value = toursData.value.map((tour) => ({
         ...tour,
         completed: false,
      }))

      toast.success("All tours have been reset")
   } catch (error) {
      console.error("Error resetting all tours:", error)
      toast.error("Failed to reset all tours")
   } finally {
      resetAllLoading.value = false
   }
}

// Load the tour data when component is mounted
onMounted(() => {
   loadTourData()
})
</script>

<style lang="scss" scoped>
.tours-table {
   max-height: 400px;
}
</style>
